import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { PanelOrder } from '@storeo/api-client';
import { MSym } from '@storeo/ui';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { money, moneyShort, relativeTime } from '../lib/format';
import { PlaceholderDot, PlaceholderTag } from '../components/lumina/Placeholder';

const ROOT_DOMAIN = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'storeo.com.br';

/** Visão Geral — redesign Lumina (docs/prototype/storeo_dashboard_do_lojista_centro_de_comando).
 *  Dado real: salesSummary, balance, pendingShipmentCount, orders, products. O resto do
 *  protótipo (chart hora-a-hora, origem de tráfego, stats de bot, conversão ao vivo) é
 *  placeholder — sem API. */
export function Inicio() {
  const { claims } = useAuth();
  const name = (claims?.name ?? claims?.email ?? 'por aqui').split(' ')[0];

  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const summary = useQuery({ queryKey: ['salesSummary'], queryFn: () => api.salesSummary() });
  const pending = useQuery({ queryKey: ['pendingShipments'], queryFn: () => api.pendingShipmentCount() });
  const recent = useQuery({ queryKey: ['orders', 0, 8], queryFn: () => api.orders({ page: 0, size: 8 }) });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });

  const today = summary.data?.todayTotal ?? 0;
  const count = summary.data?.todayCount ?? 0;
  const ticket = count > 0 ? today / count : 0;
  const storeName = store.data?.name ?? name;
  const topProducts = (products.data ?? []).filter((p) => p.status === 'published').slice(0, 3);

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      {/* header + filtros */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-space-md">
        <div>
          <div className="flex items-center gap-space-xs mb-space-3xs">
            <span className="font-label-sm text-label-sm px-space-2xs py-space-3xs rounded bg-primary-container/20 text-primary uppercase tracking-widest font-bold">
              Painel Executivo
            </span>
            <span className="text-outline text-[12px]">•</span>
            <span className="font-label-sm text-label-sm text-secondary flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" />
              Conexão em Tempo Real
            </span>
          </div>
          <h1 className="font-headline-lg text-headline-lg text-white tracking-tight">
            Centro de Comando • {storeName}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Acompanhe suas vendas em tempo real, automações ativas e o crescimento escalável da sua marca.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-space-xs">
          <div className="flex items-center p-1 bg-surface-container-low rounded-xl">
            <button className="px-space-sm py-1.5 rounded-lg bg-surface-container-high text-on-surface font-label-md text-label-md font-semibold shadow-sm transition-all flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_6px_rgba(78,222,163,0.8)]" />
              Hoje (Tempo Real)
            </button>
            <button className="px-space-sm py-1.5 rounded-lg text-on-surface-variant hover:text-on-surface font-label-md text-label-md transition-all">
              Últimos 7 dias
            </button>
            <button className="px-space-sm py-1.5 rounded-lg text-on-surface-variant hover:text-on-surface font-label-md text-label-md transition-all">
              Este mês
            </button>
          </div>
          <button className="flex items-center gap-space-2xs px-space-sm py-2 rounded-xl bg-surface-container-low hover:bg-surface-container text-on-surface font-label-md text-label-md transition-all shadow-sm">
            <MSym name="tune" className="text-[18px] text-outline" />
            <span>Filtrar</span>
          </button>
        </div>
      </div>

      {/* live banner — placeholder */}
      <div className="relative overflow-hidden rounded-xl bg-linear-to-r from-surface-container-low via-surface-container to-surface-container-low p-space-sm flex flex-col md:flex-row md:items-center md:justify-between gap-space-sm shadow-md">
        <div className="flex items-center gap-space-sm">
          <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-secondary/10">
            <span className="w-3 h-3 rounded-full bg-secondary shadow-[0_0_12px_rgba(78,222,163,0.9)]" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-space-2xs sm:gap-space-sm">
            <span className="font-label-lg text-label-lg font-bold text-on-surface">
              {count > 0 ? `${count} pedido(s) aprovado(s) hoje` : 'Sua vitrine está no ar'}
            </span>
            <span className="hidden sm:inline text-outline">•</span>
            <span className="font-body-sm text-body-sm text-on-surface-variant">
              Repasse por venda: <strong className="text-secondary font-bold">92,01%</strong> (comissão Storeo de 7,99%)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-space-md text-on-surface-variant font-label-sm text-label-sm">
          <div className="flex items-center gap-1.5">
            <MSym name="bolt" className="text-[16px] text-primary" />
            <span>
              Checkout: <strong>PIX &amp; Cartão</strong>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MSym name="security" className="text-[16px] text-secondary" />
            <span>
              Antifraude: <strong>Ativo</strong>
            </span>
          </div>
        </div>
      </div>

      {/* 4 KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md">
        <KpiCard label="Faturamento Bruto (hoje)" value={`R$ ${moneyShort(today)}`} icon="payments" iconWrap="bg-primary/10 text-primary">
          <div className="flex items-center justify-between pt-space-xs">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/15 text-secondary font-label-sm text-label-sm font-bold">
              <MSym name="calendar_today" className="text-[14px]" />
              Semana: R$ {moneyShort(summary.data?.weekTotal ?? 0)}
            </span>
          </div>
        </KpiCard>

        <KpiCard label="Pedidos Aprovados (hoje)" value={String(count)} icon="shopping_cart_checkout" iconWrap="bg-secondary/10 text-secondary">
          <div className="space-y-1.5 pt-space-xs">
            <div className="flex items-center justify-between font-label-sm text-label-sm">
              <span className="text-on-surface-variant">A receber</span>
              <span className="font-bold text-secondary">
                <BalanceValue />
              </span>
            </div>
            <Link to="/dinheiro" className="font-label-sm text-label-sm text-primary hover:underline">
              Ver extrato →
            </Link>
          </div>
        </KpiCard>

        <KpiCard label="Ticket Médio (hoje)" value={`R$ ${moneyShort(ticket)}`} icon="price_check" iconWrap="bg-tertiary/10 text-tertiary">
          <div className="mt-space-sm p-2 rounded-lg bg-surface-container flex items-center justify-between font-label-sm text-label-sm">
            <span className="text-on-surface-variant">Produtos publicados</span>
            <span className="font-bold text-on-surface">{products.data?.filter((p) => p.status === 'published').length ?? 0}</span>
          </div>
        </KpiCard>

        <KpiCard label="Recuperação Automática" value="R$ 18.420,00" icon="smart_toy" iconWrap="bg-secondary/10 text-secondary" placeholder>
          <div className="space-y-1.5 pt-space-xs">
            <div className="flex items-center justify-between font-label-sm text-label-sm">
              <span className="text-on-surface-variant">Taxa de Resgate</span>
              <span className="font-bold text-secondary">38.6%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-surface-variant overflow-hidden">
              <div className="bg-secondary h-full rounded-full w-[38%]" />
            </div>
            <span className="font-label-sm text-label-sm text-on-surface-variant">Telegram + E-mail • 149 vendas salvas</span>
          </div>
        </KpiCard>
      </div>

      {/* chart + origem de tráfego — placeholder */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-md">
        <div className="xl:col-span-8 rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col">
          <div className="flex items-center gap-space-xs mb-space-md">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Ritmo de Vendas (Hora a Hora)</h2>
            <span className="px-2 py-0.5 rounded-full bg-surface-variant text-on-surface font-label-sm text-label-sm font-bold">Ao Vivo</span>
            <PlaceholderTag />
          </div>
          <div className="relative w-full h-56">
            <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 800 240">
              <defs>
                <linearGradient id="dashToday" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#8083ff" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#8083ff" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="dashStroke" x1="0" x2="1" y1="0" y2="0">
                  <stop offset="0%" stopColor="#4cd7f6" />
                  <stop offset="50%" stopColor="#8083ff" />
                  <stop offset="100%" stopColor="#4edea3" />
                </linearGradient>
              </defs>
              {[40, 100, 160].map((y) => (
                <line key={y} stroke="#31353f" strokeDasharray="4 4" strokeWidth="1" x1="0" x2="800" y1={y} y2={y} />
              ))}
              <line stroke="#31353f" strokeWidth="1" x1="0" x2="800" y1="220" y2="220" />
              <path d="M0,190 Q100,185 200,165 T400,140 T600,120 T800,90" fill="none" stroke="#464554" strokeDasharray="6 4" strokeWidth="2" />
              <path
                d="M0,210 Q60,205 120,180 T260,110 T320,60 T420,130 T540,110 T640,30 T740,70 T800,20 L800,240 L0,240 Z"
                fill="url(#dashToday)"
              />
              <path
                d="M0,210 Q60,205 120,180 T260,110 T320,60 T420,130 T540,110 T640,30 T740,70 T800,20"
                fill="none"
                stroke="url(#dashStroke)"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </svg>
            <div className="flex justify-between font-label-sm text-label-sm text-outline pt-space-2xs">
              <span>00:00</span>
              <span>06:00</span>
              <span>12:00</span>
              <span>18:00</span>
              <span>23:59</span>
            </div>
          </div>
        </div>

        <div className="xl:col-span-4 rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col">
          <div className="flex items-center justify-between mb-space-sm">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Origem de Tráfego</h2>
            <PlaceholderTag />
          </div>
          <div className="space-y-space-md mt-space-xs">
            {[
              { icon: 'link', color: 'text-primary bg-primary', label: 'Link direto / vitrine', pct: 45 },
              { icon: 'storefront', color: 'text-tertiary bg-tertiary', label: 'Orgânico', pct: 32 },
              { icon: 'send', color: 'text-secondary bg-secondary', label: 'Telegram', pct: 15 },
              { icon: 'mail', color: 'text-on-surface-variant bg-outline', label: 'E-mail', pct: 8 },
            ].map((c) => (
              <div key={c.label}>
                <div className="flex justify-between items-center mb-1">
                  <div className="flex items-center gap-2">
                    <MSym name={c.icon} className={`text-[18px] ${c.color.split(' ')[0]}`} />
                    <span className="font-label-md text-label-md font-semibold text-on-surface">{c.label}</span>
                  </div>
                  <span className="font-label-md text-label-md font-bold text-on-surface">{c.pct}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-surface-variant overflow-hidden">
                  <div className={`h-full rounded-full ${c.color.split(' ')[1]}`} style={{ width: `${c.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* transações + right rail */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-md">
        <div className="xl:col-span-7 rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-space-xs">
              <span className="w-2.5 h-2.5 rounded-full bg-secondary shadow-[0_0_8px_rgba(78,222,163,0.8)] animate-pulse" />
              <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Últimas Transações &amp; Pedidos</h2>
            </div>
            <Link to="/vendas" className="font-label-md text-label-md text-primary hover:text-primary-fixed flex items-center gap-1 font-semibold transition-colors">
              Ver todos <MSym name="arrow_forward" className="text-[16px]" />
            </Link>
          </div>

          {!recent.data?.items.length ? (
            <p className="font-body-md text-body-md text-on-surface-variant py-space-lg text-center">Nenhuma venda ainda.</p>
          ) : (
            <div className="divide-y divide-surface-variant/40">
              {recent.data.items.map((o) => (
                <OrderRow key={o.orderId} o={o} />
              ))}
            </div>
          )}

          {pending.data ? (
            <div className="p-space-sm rounded-xl bg-surface-container flex items-center justify-between">
              <div className="flex items-center gap-space-xs text-on-surface-variant font-label-sm text-label-sm">
                <MSym name="local_shipping" className="text-[18px] text-secondary" />
                <span>
                  <strong>{pending.data}</strong> pedido(s) físico(s) aguardando envio
                </span>
              </div>
              <Link
                to="/vendas"
                className="px-space-sm py-1 rounded-lg bg-surface-container-high hover:bg-surface-variant text-on-surface font-label-sm text-label-sm font-semibold transition-colors"
              >
                Ver vendas
              </Link>
            </div>
          ) : null}
        </div>

        <div className="xl:col-span-5 space-y-space-md">
          <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md">
            <div className="flex items-center justify-between mb-space-sm">
              <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">Produtos publicados</h3>
              <Link to="/produtos" className="font-label-sm text-label-sm text-primary font-semibold hover:underline">
                Ver catálogo
              </Link>
            </div>
            <div className="space-y-space-sm">
              {topProducts.length === 0 ? (
                <p className="font-body-sm text-body-sm text-on-surface-variant">Nenhum produto publicado ainda.</p>
              ) : (
                topProducts.map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between p-2 rounded-xl bg-surface-container hover:bg-surface-container-high transition-colors">
                    <div className="flex items-center gap-space-sm min-w-0">
                      <span className="w-12 h-12 rounded-lg bg-linear-to-br from-primary-container/40 to-tertiary-container/40 shrink-0 flex items-center justify-center text-on-surface-variant">
                        <MSym name={p.deliveryChannel === 'physical' ? 'inventory_2' : 'bolt'} className="text-[20px]" />
                      </span>
                      <div className="min-w-0">
                        <span className="font-label-md text-label-md font-bold text-on-surface block truncate">{p.name}</span>
                        <span className="font-label-sm text-label-sm text-outline">
                          {p.deliveryChannel === 'physical' ? 'Produto físico' : 'Infoproduto'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-label-md text-label-md font-extrabold text-on-surface block">R$ {moneyShort(p.amount)}</span>
                      <span className="font-label-sm text-label-sm text-secondary font-semibold">#{i + 1}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md">
            <div className="flex items-center justify-between mb-space-sm">
              <div className="flex items-center gap-space-xs">
                <MSym name="precision_manufacturing" className="text-secondary text-[20px]" />
                <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">Automações &amp; Bots</h3>
              </div>
              <PlaceholderTag />
            </div>
            <div className="space-y-space-xs">
              {[
                { icon: 'send', color: 'bg-secondary/15 text-secondary', title: 'Bot Telegram de Recuperação', sub: '84 abordagens • 32 convertidos', tag: 'Ativo', tagCls: 'bg-secondary-container/20 text-secondary' },
                { icon: 'mail', color: 'bg-primary/15 text-primary', title: 'Sequência de Boas-vindas', sub: '68% abertura • 18% CTR', tag: 'Ativo', tagCls: 'bg-primary-container/20 text-primary' },
                { icon: 'notifications_active', color: 'bg-tertiary/15 text-tertiary', title: 'Notificação de venda no Telegram', sub: 'A cada venda aprovada', tag: 'Sinc.', tagCls: 'bg-tertiary-container/20 text-tertiary' },
              ].map((a) => (
                <div key={a.title} className="p-space-sm rounded-xl bg-surface-container flex items-center justify-between gap-space-xs">
                  <div className="flex items-center gap-space-sm min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${a.color}`}>
                      <MSym name={a.icon} className="text-[18px]" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-label-md text-label-md font-semibold text-on-surface block truncate">{a.title}</span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">{a.sub}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded font-label-sm text-label-sm font-bold shrink-0 ${a.tagCls}`}>{a.tag}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-surface-container-low p-space-md shadow-md flex flex-col gap-space-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-space-xs">
                <MSym name="language" className="text-primary text-[20px]" />
                <span className="font-label-lg text-label-lg font-bold text-on-surface">Domínio da Loja &amp; SSL</span>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-secondary-container/20 text-secondary font-label-sm text-label-sm font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary" /> SSL Ativo
              </span>
            </div>
            <div className="p-space-sm rounded-xl bg-surface-container flex items-center justify-between gap-space-xs">
              <div className="flex flex-col min-w-0">
                <span className="font-label-sm text-label-sm text-on-surface-variant">Endereço da loja</span>
                <span className="font-label-md text-label-md font-semibold text-on-surface truncate">
                  {store.data?.subdomain ? `${store.data.subdomain}.${ROOT_DOMAIN}` : ROOT_DOMAIN}
                </span>
              </div>
              <Link
                to="/loja"
                className="px-space-sm py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary font-label-sm text-label-sm font-bold transition-all whitespace-nowrap shrink-0"
              >
                Personalizar
              </Link>
            </div>
          </div>

          <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md">
            <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider block mb-space-sm">Ações Rápidas</span>
            <div className="grid grid-cols-2 gap-space-xs">
              {[
                { to: '/produtos', icon: 'inventory_2', wrap: 'bg-secondary-container text-on-secondary-container', title: 'Novo Produto', sub: 'Físico ou Digital' },
                { to: '/loja', icon: 'palette', wrap: 'bg-primary-container text-on-primary-container', title: 'Personalizar Loja', sub: 'Modelo, cores, seções' },
                { to: '/campanhas', icon: 'send', wrap: 'bg-surface-variant text-secondary', title: 'Nova Campanha', sub: 'Broadcast no Telegram' },
                { to: '/dinheiro', icon: 'account_balance_wallet', wrap: 'bg-surface-variant text-tertiary', title: 'Dinheiro', sub: 'Saldo e extrato' },
              ].map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className="flex items-center gap-space-xs p-space-sm rounded-xl bg-surface-container hover:bg-surface-container-high text-on-surface transition-all text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform shrink-0 ${a.wrap}`}>
                    <MSym name={a.icon} className="text-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-label-md text-label-md font-semibold block text-on-surface truncate">{a.title}</span>
                    <span className="font-label-sm text-label-sm text-outline">{a.sub}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BalanceValue() {
  const balance = useQuery({ queryKey: ['balance'], queryFn: () => api.balance() });
  return <>{balance.data ? money(balance.data.available) : '—'}</>;
}

function KpiCard({
  label,
  value,
  icon,
  iconWrap,
  placeholder,
  children,
}: {
  label: string;
  value: string;
  icon: string;
  iconWrap: string;
  placeholder?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl bg-surface-container-low p-space-lg shadow-md hover:bg-surface-container/70 transition-all flex flex-col justify-between overflow-hidden group">
      <div className="flex items-start justify-between mb-space-sm">
        <div>
          <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider block mb-1">
            {label}
            {placeholder ? <PlaceholderDot /> : null}
          </span>
          <span className="font-metric-stat text-metric-stat text-white font-extrabold tracking-tight">{value}</span>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform ${iconWrap}`}>
          <MSym name={icon} className="text-[22px]" />
        </div>
      </div>
      {children}
    </div>
  );
}

function OrderRow({ o }: { o: PanelOrder }) {
  const isPix = o.paymentMethod !== 'card';
  const isPhysical = o.deliveryChannel === 'physical';
  return (
    <div className="py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-space-xs hover:bg-surface-container/50 px-2 rounded-lg transition-colors">
      <div className="flex items-center gap-space-sm min-w-0">
        <div className={`w-10 h-10 rounded-lg bg-surface-container flex items-center justify-center shrink-0 ${isPix ? 'text-secondary' : 'text-primary'}`}>
          <MSym name={isPix ? 'check_circle' : 'credit_card'} className="text-[20px]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-label-lg text-label-lg font-bold text-on-surface">#{o.number}</span>
            <span className="px-1.5 py-0.5 rounded bg-surface-variant font-label-sm text-label-sm text-outline font-semibold">
              {isPhysical ? 'Físico' : 'Digital'}
            </span>
            <span className="font-label-md text-label-md text-on-surface truncate">{o.productName}</span>
          </div>
          <div className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
            <span className="truncate">{o.customerName || o.customerEmail}</span>
            <span>•</span>
            <span className="text-outline">{relativeTime(o.createdAt)}</span>
          </div>
        </div>
      </div>
      <div className="flex sm:flex-col sm:items-end justify-between items-center shrink-0">
        <span className="font-label-lg text-label-lg font-extrabold text-on-surface">{money(o.amount)}</span>
        <span
          className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold flex items-center gap-1 ${
            isPix ? 'bg-secondary-container/25 text-secondary' : 'bg-primary-container/20 text-primary'
          }`}
        >
          <MSym name={isPix ? 'flash_on' : 'credit_card'} className="text-[12px]" /> {isPix ? 'PIX' : 'Cartão'}
        </span>
      </div>
    </div>
  );
}

